import { Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';

export enum TileType {
  EMPTY = 0,
  WALL = 1,
  DOT = 2,
  POWER_PELLET = 3,
  GHOST_HOUSE = 4,
  TUNNEL = 5
}

export interface MazeTile {
  type: TileType;
  x: number;
  y: number;
  collected?: boolean;
}

export class MazeSystem {
  private maze: TileType[][] = [];
  private tiles: MazeTile[][] = [];
  private renderManager: RenderManager;
  private tileSize: number = 24; // Increased from 16 for better mobile visibility
  private mazeWidth: number = 25;
  private mazeHeight: number = 31;
  
  // Maze colors (C64 palette)
  private readonly WALL_COLOR = '#62D0FE';
  private readonly DOT_COLOR = '#BFCE72';
  private readonly POWER_PELLET_COLOR = '#BFCE72';
  private readonly TUNNEL_COLOR = '#787878';

  constructor(renderManager: RenderManager) {
    this.renderManager = renderManager;
    this.createClassicMaze();
  }

  private createClassicMaze(): void {
    // Classic Pac-Man style maze layout (25x31 grid)
    const mazeLayout = [
      [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
      [1,2,2,2,2,2,2,2,2,2,2,2,1,2,2,2,2,2,2,2,2,2,2,2,1],
      [1,3,1,1,1,2,1,1,1,1,1,2,1,2,1,1,1,1,1,2,1,1,1,3,1],
      [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
      [1,2,1,1,1,2,1,2,1,1,1,1,1,1,1,1,1,2,1,2,1,1,1,2,1],
      [1,2,2,2,2,2,1,2,2,2,2,2,1,2,2,2,2,2,1,2,2,2,2,2,1],
      [1,1,1,1,1,2,1,1,1,1,1,0,1,0,1,1,1,1,1,2,1,1,1,1,1],
      [1,1,1,1,1,2,1,0,0,0,0,0,0,0,0,0,0,0,1,2,1,1,1,1,1],
      [1,1,1,1,1,2,1,0,1,1,4,4,4,4,4,1,1,0,1,2,1,1,1,1,1],
      [5,0,0,0,0,2,0,0,1,4,4,4,4,4,4,4,1,0,0,2,0,0,0,0,5],
      [1,1,1,1,1,2,1,0,1,4,4,4,4,4,4,4,1,0,1,2,1,1,1,1,1],
      [1,1,1,1,1,2,1,0,1,1,1,1,1,1,1,1,1,0,1,2,1,1,1,1,1],
      [1,1,1,1,1,2,1,0,0,0,0,0,0,0,0,0,0,0,1,2,1,1,1,1,1],
      [1,1,1,1,1,2,1,1,1,1,1,0,1,0,1,1,1,1,1,2,1,1,1,1,1],
      [1,2,2,2,2,2,2,2,2,2,2,2,1,2,2,2,2,2,2,2,2,2,2,2,1],
      [1,2,1,1,1,2,1,1,1,1,1,2,1,2,1,1,1,1,1,2,1,1,1,2,1],
      [1,3,2,2,1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1,2,2,3,1],
      [1,1,1,2,1,2,1,2,1,1,1,1,1,1,1,1,1,2,1,2,1,2,1,1,1],
      [1,2,2,2,2,2,1,2,2,2,2,2,1,2,2,2,2,2,1,2,2,2,2,2,1],
      [1,2,1,1,1,1,1,1,1,1,1,2,1,2,1,1,1,1,1,1,1,1,1,2,1],
      [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
      [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]
    ];

    this.maze = mazeLayout;
    this.mazeHeight = mazeLayout.length;
    this.mazeWidth = mazeLayout[0].length;

    // Create tile objects
    this.tiles = [];
    for (let y = 0; y < this.mazeHeight; y++) {
      this.tiles[y] = [];
      for (let x = 0; x < this.mazeWidth; x++) {
        this.tiles[y][x] = {
          type: this.maze[y][x] as TileType,
          x: x,
          y: y,
          collected: false
        };
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D, offsetX: number = 0, offsetY: number = 0): void {
    for (let y = 0; y < this.mazeHeight; y++) {
      for (let x = 0; x < this.mazeWidth; x++) {
        const tile = this.tiles[y][x];
        const screenX = offsetX + x * this.tileSize;
        const screenY = offsetY + y * this.tileSize;

        this.renderTile(tile, screenX, screenY);
      }
    }
  }

  private renderTile(tile: MazeTile, x: number, y: number): void {
    switch (tile.type) {
      case TileType.WALL:
        this.renderWall(x, y);
        break;
      
      case TileType.DOT:
        if (!tile.collected) {
          this.renderDot(x, y);
        }
        break;
      
      case TileType.POWER_PELLET:
        if (!tile.collected) {
          this.renderPowerPellet(x, y);
        }
        break;
      
      case TileType.TUNNEL:
        this.renderTunnel(x, y);
        break;
      
      case TileType.GHOST_HOUSE:
        this.renderGhostHouse(x, y);
        break;
    }
  }

  private renderWall(x: number, y: number): void {
    // Draw wall segments with connecting logic
    const tileX = Math.floor(x / this.tileSize);
    const tileY = Math.floor(y / this.tileSize);
    
    // Check adjacent tiles to determine wall shape
    const hasTop = this.isWall(tileX, tileY - 1);
    const hasBottom = this.isWall(tileX, tileY + 1);
    const hasLeft = this.isWall(tileX - 1, tileY);
    const hasRight = this.isWall(tileX + 1, tileY);
    
    // Draw basic wall block
    this.renderManager.fillRect(x, y, this.tileSize, this.tileSize, this.WALL_COLOR);
    
    // Add border details for classic maze look
    if (!hasTop) {
      this.renderManager.fillRect(x, y, this.tileSize, 2, '#FFFFFF');
    }
    if (!hasBottom) {
      this.renderManager.fillRect(x, y + this.tileSize - 2, this.tileSize, 2, '#FFFFFF');
    }
    if (!hasLeft) {
      this.renderManager.fillRect(x, y, 2, this.tileSize, '#FFFFFF');
    }
    if (!hasRight) {
      this.renderManager.fillRect(x + this.tileSize - 2, y, 2, this.tileSize, '#FFFFFF');
    }
  }

  private renderDot(x: number, y: number): void {
    const centerX = x + this.tileSize / 2;
    const centerY = y + this.tileSize / 2;
    this.renderManager.drawCircle(centerX, centerY, 1, true, this.DOT_COLOR);
  }

  private renderPowerPellet(x: number, y: number): void {
    const centerX = x + this.tileSize / 2;
    const centerY = y + this.tileSize / 2;
    
    // Animated pulsing power pellet
    const time = Date.now() * 0.005;
    const radius = 4 + Math.sin(time) * 1;
    this.renderManager.drawCircle(centerX, centerY, radius, true, this.POWER_PELLET_COLOR);
  }

  private renderTunnel(x: number, y: number): void {
    this.renderManager.fillRect(x, y + 6, this.tileSize, 4, this.TUNNEL_COLOR);
  }

  private renderGhostHouse(x: number, y: number): void {
    this.renderManager.fillRect(x, y, this.tileSize, this.tileSize, '#8B3F96');
    this.renderManager.strokeRect(x, y, this.tileSize, this.tileSize, '#FFFFFF', 1);
  }

  // Maze navigation and collision methods
  public isWalkable(gridX: number, gridY: number): boolean {
    if (gridX < 0 || gridX >= this.mazeWidth || gridY < 0 || gridY >= this.mazeHeight) {
      // Handle tunnel positions
      if (gridY === 9 && (gridX < 0 || gridX >= this.mazeWidth)) {
        return true;
      }
      return false;
    }
    
    const tileType = this.maze[gridY][gridX];
    return tileType === TileType.EMPTY || tileType === TileType.DOT || 
           tileType === TileType.POWER_PELLET || tileType === TileType.TUNNEL ||
           tileType === TileType.GHOST_HOUSE;
  }

  private isWall(gridX: number, gridY: number): boolean {
    if (gridX < 0 || gridX >= this.mazeWidth || gridY < 0 || gridY >= this.mazeHeight) {
      return false;
    }
    return this.maze[gridY][gridX] === TileType.WALL;
  }

  public isTunnel(gridX: number, gridY: number): boolean {
    if (gridX < 0 || gridX >= this.mazeWidth || gridY < 0 || gridY >= this.mazeHeight) {
      return false;
    }
    return this.maze[gridY][gridX] === TileType.TUNNEL;
  }

  public getTileAt(gridX: number, gridY: number): MazeTile | null {
    if (gridX < 0 || gridX >= this.mazeWidth || gridY < 0 || gridY >= this.mazeHeight) {
      return null;
    }
    return this.tiles[gridY][gridX];
  }

  public collectDot(gridX: number, gridY: number): number {
    const tile = this.getTileAt(gridX, gridY);
    if (!tile || tile.collected) return 0;
    
    if (tile.type === TileType.DOT) {
      tile.collected = true;
      return 10; // Points for regular dot
    } else if (tile.type === TileType.POWER_PELLET) {
      tile.collected = true;
      return 50; // Points for power pellet
    }
    
    return 0;
  }

  public isPowerPellet(gridX: number, gridY: number): boolean {
    const tile = this.getTileAt(gridX, gridY);
    return tile !== null && tile.type === TileType.POWER_PELLET && !tile.collected;
  }

  // Convert between screen and grid coordinates
  public screenToGrid(screenX: number, screenY: number): Vector2 {
    return {
      x: Math.floor(screenX / this.tileSize),
      y: Math.floor(screenY / this.tileSize)
    };
  }

  public gridToScreen(gridX: number, gridY: number): Vector2 {
    return {
      x: gridX * this.tileSize,
      y: gridY * this.tileSize
    };
  }

  public getTileSize(): number {
    return this.tileSize;
  }

  public getMazeWidth(): number {
    return this.mazeWidth;
  }

  public getMazeHeight(): number {
    return this.mazeHeight;
  }

  // Check if all dots are collected (level complete)
  public areAllDotsCollected(): boolean {
    for (let y = 0; y < this.mazeHeight; y++) {
      for (let x = 0; x < this.mazeWidth; x++) {
        const tile = this.tiles[y][x];
        if ((tile.type === TileType.DOT || tile.type === TileType.POWER_PELLET) && !tile.collected) {
          return false;
        }
      }
    }
    return true;
  }

  public getRemainingDots(): number {
    let count = 0;
    for (let y = 0; y < this.mazeHeight; y++) {
      for (let x = 0; x < this.mazeWidth; x++) {
        const tile = this.tiles[y][x];
        if ((tile.type === TileType.DOT || tile.type === TileType.POWER_PELLET) && !tile.collected) {
          count++;
        }
      }
    }
    return count;
  }

  // Handle tunnel wrapping
  public handleTunnelWrap(gridX: number, gridY: number): Vector2 {
    if (gridY === 9) {
      // Entering left tunnel (going left from position 0 or beyond)
      if (gridX <= 0) {
        return { x: 23, y: gridY }; // Exit at right side safe position
      }
      // Entering right tunnel (going right from position 24 or beyond)
      if (gridX >= this.mazeWidth - 1) {
        return { x: 1, y: gridY }; // Exit at left side safe position
      }
    }
    
    return { x: gridX, y: gridY };
  }

  // Check if a position is a tunnel entrance
  public isTunnelEntrance(gridX: number, gridY: number, direction: { x: number, y: number }): boolean {
    // Check if we're at a tunnel position trying to move into the tunnel
    if (gridY === 9) {
      // Moving left from left tunnel entrance (position 0,9)
      if (gridX === 0 && direction.x === -1) {
        return true;
      }
      // Moving right from right tunnel entrance (position 24,9) 
      if (gridX === this.mazeWidth - 1 && direction.x === 1) {
        return true;
      }
      // Moving right toward left tunnel (from position 1,9 to 0,9)
      if (gridX === 1 && direction.x === -1) {
        return true;
      }
      // Moving left toward right tunnel (from position 23,9 to 24,9)
      if (gridX === 23 && direction.x === 1) {
        return true;
      }
    }
    return false;
  }

  // Reset maze for new game/level
  public reset(): void {
    for (let y = 0; y < this.mazeHeight; y++) {
      for (let x = 0; x < this.mazeWidth; x++) {
        this.tiles[y][x].collected = false;
      }
    }
  }

  // Get valid directions from a position (for AI pathfinding)
  public getValidDirections(gridX: number, gridY: number): Vector2[] {
    const directions: Vector2[] = [];
    const possibleMoves = [
      { x: 0, y: -1 }, // Up
      { x: 1, y: 0 },  // Right  
      { x: 0, y: 1 },  // Down
      { x: -1, y: 0 }  // Left
    ];
    
    for (const move of possibleMoves) {
      const newX = gridX + move.x;
      const newY = gridY + move.y;
      
      // Handle tunnel wrapping
      const wrappedPos = this.handleTunnelWrap(newX, newY);
      
      if (this.isWalkable(wrappedPos.x, wrappedPos.y)) {
        directions.push(move);
      }
    }
    
    return directions;
  }
}